import React, { useState, useEffect } from 'react';
import { Input, Switch, Space, Typography, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import {
  StyledCard,
  StyledSaveButton,
  FullWidthSpaceVertical,
  CursorPointerIcon,
  CursorPointerText,
  TitleNoMargin,
  FullWidthSpaceLarge,
  HelperTextContainer,
  SecondaryTextSmall,
  InputNumberFullWidth,
  InputNumberFullWidthMargin,
} from './Styles';
import { CustomizationRulesProps } from './types';
import { SUBMISSION_TYPES } from './constants';

const { Text } = Typography;

const CustomizationRules: React.FC<CustomizationRulesProps> = ({
  customizationData,
  handleBackClick,
  handleSubmit,
  handleInputChange,
  width,
}) => {
  const [minQuantity, setMinQuantity] = useState(customizationData.min_quantity || 0);
  const [maxQuantity, setMaxQuantity] = useState(customizationData.max_quantity || 0);
  const [multipleSelections, setMultipleSelections] = useState(
    customizationData.multiple_selections || false
  );
  const [multipleSelectionsQuantity, setMultipleSelectionsQuantity] = useState(
    customizationData.multiple_selections_quantity || 0
  );
  const [note, setNote] = useState(customizationData.note || '');
  const [isSaving, setIsSaving] = useState(false);

  // Update state when customizationData changes
  useEffect(() => {
    console.log('🔄 CustomizationRules - customizationData updated:', customizationData);
    setMinQuantity(customizationData.min_quantity || 0);
    setMaxQuantity(customizationData.max_quantity || 0);
    setMultipleSelections(customizationData.multiple_selections || false);
    setMultipleSelectionsQuantity(customizationData.multiple_selections_quantity || 0);
    setNote(customizationData.note || '');
  }, [customizationData]);

  const saveChanges = async () => {
    setIsSaving(true);
    try {
      const updatedCustomizationData = {
        ...customizationData,
        min_quantity: minQuantity,
        max_quantity: maxQuantity,
        multiple_selections: multipleSelections,
        multiple_selections_quantity: multipleSelectionsQuantity,
        note: note,
      };
      await handleSubmit(SUBMISSION_TYPES.CUSTOMIZATION_RULES, updatedCustomizationData);
    } finally {
      setIsSaving(false);
    }
  };

  const isFixed = !width;

  return (
    <StyledCard $width={width} $isFixed={isFixed}>
      <FullWidthSpaceVertical direction="vertical" size="middle">
        <Space>
          <CursorPointerIcon onClick={handleBackClick}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </CursorPointerIcon>
          <CursorPointerText onClick={handleBackClick}>
            Customizations
          </CursorPointerText>
          <Text> / Customization rules</Text>
        </Space>

        <TitleNoMargin level={4}>Customisation rules</TitleNoMargin>

        <Text type="secondary">Set limits for customising items</Text>

        <FullWidthSpaceLarge direction="vertical" size="large">
          <div>
            <Text strong>Minimum quantity</Text>
            <HelperTextContainer>
              <SecondaryTextSmall type="secondary">
                What's the minimum number of options a customer must select?
              </SecondaryTextSmall>
            </HelperTextContainer>
            <InputNumberFullWidth
              min={0}
              value={minQuantity}
              onChange={(value) => {
                const numValue = typeof value === 'number' ? value : 0;
                setMinQuantity(numValue);
                handleInputChange('min_quantity', numValue);
              }}
            />
          </div>

          <div>
            <Text strong>Maximum quantity</Text>
            <HelperTextContainer>
              <SecondaryTextSmall type="secondary">
                What's the maximum number of options a customer can select?
              </SecondaryTextSmall>
            </HelperTextContainer>
            <InputNumberFullWidth
              min={1}
              value={maxQuantity}
              onChange={(value) => {
                const numValue = typeof value === 'number' ? value : 0;
                setMaxQuantity(numValue);
                handleInputChange('max_quantity', numValue);
              }}
            />
          </div>

          <div>
            <Text strong>Multiple selections</Text>
            <HelperTextContainer>
              <SecondaryTextSmall type="secondary">
                Can customers select the options more than once?
              </SecondaryTextSmall>
            </HelperTextContainer>
            <Switch
              checked={multipleSelections}
              onChange={(checked) => {
                handleInputChange('multiple_selections', checked);
                setMultipleSelections(checked);
              }}
            />
          </div>

          {multipleSelections && (
            <div>
              <Text strong>
                How many times can customers select any single option?
              </Text>
              <InputNumberFullWidthMargin
                min={1}
                value={multipleSelectionsQuantity}
                onChange={(value) => {
                  const numValue = typeof value === 'number' ? value : 0;
                  setMultipleSelectionsQuantity(numValue);
                  handleInputChange('multiple_selections_quantity', numValue);
                }}
              />
            </div>
          )}

          <div>
            <Text strong>Note</Text>
            <HelperTextContainer>
              <SecondaryTextSmall type="secondary">
                Add any additional notes or instructions for this customization
              </SecondaryTextSmall>
            </HelperTextContainer>
            <Input
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                handleInputChange('note', e.target.value);
              }}
              placeholder="Enter notes or instructions"
            />
          </div>
        </FullWidthSpaceLarge>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
          <StyledSaveButton 
            onClick={saveChanges}
            loading={isSaving}
            icon={isSaving ? <Spin size="small" /> : null}
            style={{ width: 'auto' }}
          >
            Save
          </StyledSaveButton>
        </div>
      </FullWidthSpaceVertical>
    </StyledCard>
  );
};

export default CustomizationRules;
